/**
 * One-click cart upgrade: swaps a single 60-capsule bottle for the 180-capsule pack.
 *
 * The button is rendered by snippets/cart-upsell.liquid under the cart line. Clicking it adds the
 * 180 pack (keeping the line's subscription plan, if any) and only then removes the single bottle,
 * so a failed add never leaves the customer with an emptier cart. The cart drawer / cart page is
 * re-rendered from the removal's response through the theme's own `cart:update` event.
 *
 * Uses event delegation because the cart markup is re-rendered (morphed) on every cart change.
 */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const EVENT_SOURCE = 'cart-upsell';

  const request = (path, body) =>
    fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `${path} responded ${response.status}`);
      }
      return data;
    });

  const cartSectionIds = () => [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId)
    ),
  ];

  const refreshCart = (cart) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || {},
          sourceId: EVENT_SOURCE,
          data: {
            source: EVENT_SOURCE,
            itemCount: cart ? cart.item_count : undefined,
            sections: cart ? cart.sections : undefined,
          },
        },
      })
    );
  };

  const upgrade = async (button) => {
    const card = button.closest('[data-cart-upsell-card]');
    const error = card ? card.querySelector('.cart-upsell__error') : null;
    const { lineKey, targetVariant, sellingPlan } = button.dataset;
    const label = button.textContent;
    let added = false;

    button.disabled = true;
    button.textContent = 'Upgrading...';
    if (error) error.hidden = true;

    try {
      const item = { id: Number(targetVariant), quantity: 1 };
      if (sellingPlan) item.selling_plan = Number(sellingPlan);

      // Add the 180 pack first: if that fails, the single bottle is still in the cart.
      await request('cart/add.js', { items: [item] });
      added = true;

      const cart = await request('cart/change.js', {
        id: lineKey,
        quantity: 0,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      });

      refreshCart(cart);
    } catch (err) {
      console.error('[cart-upsell]', err);

      if (added) {
        // The 180 pack went in but the single bottle couldn't be removed: show the real cart.
        refreshCart(null);
        return;
      }

      button.disabled = false;
      button.textContent = label;
      if (error) {
        error.textContent = "Sorry, we couldn't upgrade that just now. Please try again.";
        error.hidden = false;
      }
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-upsell]') : null;
    if (!button || button.disabled) return;

    event.preventDefault();
    upgrade(button);
  });
})();
